"use client";

import {useCallback} from "react";
import {useTranslation} from "react-i18next";
import {useAlert} from "@/hooks/useAlert";
import {DownloadError, downloadGeneratedFile} from "@/utils/downloadGeneratedFile";

/**
 * UI wrapper around `downloadGeneratedFile` that owns the failure UX. Returns
 * a fire-and-forget callback so call sites stay simple — the hook handles
 * mapping `DownloadError.status` to a localized toast and logging the raw
 * error for observability. Keeping this layer separate from the pure helper
 * means the helper stays trivially unit-testable without dragging the toast
 * system into its tests.
 */
export const useArtifactDownload = (): ((url: string, fileName: string) => void) => {
   const {setAlert} = useAlert();
   const {t} = useTranslation("ask");

   return useCallback(
      (url: string, fileName: string) => {
         void downloadGeneratedFile(url, fileName).catch((err: unknown) => {
            console.warn("Artifact download failed:", err);
            const message =
               err instanceof DownloadError && err.status === 401
                  ? t("download.sessionExpired")
                  : err instanceof DownloadError && err.status === 404
                     ? t("download.fileNotFound")
                     : t("download.failed");
            setAlert({status: "error", message, secondsToClose: 10});
         });
      },
      [setAlert, t]
   );
};
